import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FlaskConical, X, Search, Check } from "lucide-react";

const MODES = [["off", "Off (admins only)"], ["all", "On for everyone"], ["specific", "On for specific users"]];

function FeatureRow({ feature, onSaved }) {
  const [mode, setMode] = useState(feature.mode || "off");
  const [users, setUsers] = useState(feature.users || []);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);

  const search = useCallback(async (term) => {
    if (!term) { setResults([]); return; }
    try {
      const res = await axios.get(`${API}/admin/beta/users`, { params: { q: term } });
      setResults(res.data.users || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { const t = setTimeout(() => search(q), 300); return () => clearTimeout(t); }, [q, search]);

  const addUser = (u) => { if (!users.includes(u)) setUsers([...users, u]); setQ(""); setResults([]); };
  const removeUser = (u) => setUsers(users.filter((x) => x !== u));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/beta/features/${feature.key}`, { mode, users: mode === "specific" ? users : [] });
      toast.success(`${feature.name} rollout updated`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`beta-feature-${feature.key}`} style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "18px", marginBottom: "14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
        <div>
          <div style={{ fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
            {feature.name}
            <span style={{ fontSize: "10px", fontWeight: 700, color: "#7c3aed", background: "#ede9fe", padding: "2px 8px", borderRadius: "10px" }}>BETA</span>
          </div>
          <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px", maxWidth: "520px" }}>{feature.description}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: "8px", marginTop: "14px", flexWrap: "wrap" }}>
        {MODES.map(([v, l]) => (
          <button key={v} data-testid={`beta-mode-${feature.key}-${v}`} onClick={() => setMode(v)}
            style={{ padding: "7px 14px", borderRadius: "9px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
              border: mode === v ? "1px solid #7c3aed" : "1px solid #e2e8f0",
              background: mode === v ? "linear-gradient(135deg,#ede9fe,#dbeafe)" : "#fff",
              color: mode === v ? "#6d28d9" : "#64748b" }}>
            {l}
          </button>
        ))}
      </div>

      {mode === "specific" && (
        <div style={{ marginTop: "14px" }}>
          <div style={{ position: "relative", maxWidth: "360px" }}>
            <Search size={15} style={{ position: "absolute", left: "10px", top: "10px", color: "#94a3b8" }} />
            <input className="form-input" style={{ paddingLeft: "32px" }} placeholder="Search users by name or email…"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid={`beta-user-search-${feature.key}`} />
            {results.length > 0 && (
              <div style={{ position: "absolute", zIndex: 20, background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", marginTop: "4px", width: "100%", maxHeight: "200px", overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
                {results.map((u) => (
                  <div key={u.username} onClick={() => addUser(u.username)}
                    style={{ padding: "8px 12px", cursor: "pointer", fontSize: "13px" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                    <strong>{u.full_name || u.username}</strong> <span style={{ color: "#94a3b8" }}>{u.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "10px" }}>
            {users.map((u) => (
              <span key={u} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f1f5f9", color: "#334155", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: 600 }}>
                {u} <X size={12} style={{ cursor: "pointer" }} onClick={() => removeUser(u)} />
              </span>
            ))}
            {users.length === 0 && <span style={{ fontSize: "12px", color: "#94a3b8" }}>No users selected yet.</span>}
          </div>
        </div>
      )}

      <div style={{ marginTop: "14px" }}>
        <Button size="sm" data-testid={`beta-save-${feature.key}`} onClick={save} disabled={saving}
          style={{ background: "linear-gradient(135deg,#7c3aed,#4f46e5)" }}>
          <Check size={14} style={{ marginRight: "6px" }} /> {saving ? "Saving…" : "Save rollout"}
        </Button>
      </div>
    </div>
  );
}

export default function BetaFeaturesPanel() {
  const [open, setOpen] = useState(false);
  const [features, setFeatures] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/beta/features`);
      setFeatures(res.data.features || []);
    } catch (e) { toast.error("Failed to load beta features"); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <div className="content-card" style={{ marginTop: "24px" }} data-testid="beta-features-card">
      <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 className="card-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FlaskConical size={18} /> Beta Features
        </h2>
        <Button size="sm" variant="outline" data-testid="open-beta-modal-btn" onClick={() => setOpen(true)}>Manage</Button>
      </div>
      <p style={{ fontSize: "14px", color: "#64748b" }}>
        Roll out experimental features to everyone or to select users. New features start admin-only.
      </p>

      {open && (
        <div data-testid="beta-modal" onClick={() => setOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "16px", padding: "28px", maxWidth: "720px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: 800, display: "flex", alignItems: "center", gap: "10px" }}>
                <FlaskConical size={22} style={{ color: "#7c3aed" }} /> Beta Features
              </h2>
              <X size={22} style={{ cursor: "pointer", color: "#64748b" }} onClick={() => setOpen(false)} data-testid="close-beta-modal" />
            </div>
            <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "20px" }}>
              Control who can access each experimental feature. Admins always have access.
            </p>
            {features.map((f) => <FeatureRow key={f.key} feature={f} onSaved={load} />)}
            {features.length === 0 && <p style={{ color: "#94a3b8" }}>No beta features available.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
